import React from 'react';

import { FCWithChildren } from '@wix/challenges-web-library';
import { Step } from '../../../../../types/v3Types';
import { useVideoStepData } from '../../../../../hooks/useVideoStepData';
import { useSidebarLayoutBase } from '../../views/SidebarLayout/contexts/SidebarLayoutBase/SidebarLayoutBaseContext';
import { VideoAutoplayScreen } from './components/VideoAutoplayScreen';
import { VideoError } from './components/VideoError';
import { ProgramsVideoPlayer } from '@wix/challenges-web-video-player';
import { useEnvironment, useExperiments } from '@wix/yoshi-flow-editor';
import {
  ChallengeEntityType,
  isStepResolved,
  useNextEntity,
} from '../../views/utils';
import { useVideo } from '../../../../../contexts/storage-contexts/Video';

import { st, classes } from './VideoPlayerInStep.st.css';
import { Challenges } from '../../../../../editor/types/Experiments';

export interface VideoPlayerInStepProps {
  step: Step;
}

const AUTOCOMPLETE_STEP_PROGRESS_PERCENTAGE = 95;

export const VideoPlayerInStep: FCWithChildren<VideoPlayerInStepProps> = ({
  step,
}: VideoPlayerInStepProps) => {
  const { isMobile } = useEnvironment();
  const { experiments } = useExperiments();
  const isEnabledDisableSeekOnVideo = experiments.enabled(
    Challenges.disableSeekOnVideo,
  );

  const { isVideo, videoOptions, autoNavigate, requiredCompletionPercentage } =
    useVideoStepData(step);
  const {
    requestVideoData,
    videoData,
    isVideoRequestInProgress,
    videoDataRequestError,
    requestHLSStreamURL,
    HLSStreamURL,
    HLSStreamURLRequestError,
    resetVideoState,
  } = useVideo();
  const {
    isAutoPlayVideo,
    changeAutoplayOptions,
    setVideoStepAccepted,
    isVideoLoadingError,
    setIsVideoLoadingError,
    resolveCurrentStep,
    currentStep,
  } = useSidebarLayoutBase();
  const playerRef = React.useRef(null);
  const customCoverUrl = videoOptions?.coverImage?.url
    ? `https://static.wixstatic.com/${videoOptions?.coverImage?.url}`
    : null;
  const { nextEntity } = useNextEntity(step?.id);
  const isLastEntity = nextEntity.type === ChallengeEntityType.VOID;
  const isAutoPlayOn = !!autoNavigate;
  const autocompleteStepFiredRef = React.useRef(false);

  const [isAutoplayStateEnabled, setIsAutoplayStateEnabled] =
    React.useState<boolean>(false);

  // Calculate if disableSeekForward should be enabled
  const shouldDisableSeekForward = React.useMemo(() => {
    const isStepAlreadyResolved = isStepResolved(currentStep);
    const result =
      isEnabledDisableSeekOnVideo &&
      requiredCompletionPercentage > 0 &&
      !isStepAlreadyResolved;

    return result;
  }, [isEnabledDisableSeekOnVideo, requiredCompletionPercentage, currentStep]);

  const onVideoPlayerProgress = React.useCallback(
    (progress: number) => {
      const progressToAutoCompleteStep =
        requiredCompletionPercentage ?? AUTOCOMPLETE_STEP_PROGRESS_PERCENTAGE;
      const isStepAlreadyResolved = isStepResolved(currentStep);

      setVideoStepAccepted(
        requiredCompletionPercentage
          ? progress >= Math.min(requiredCompletionPercentage, 99.5)
          : true,
      );

      if (
        !isStepAlreadyResolved &&
        Math.ceil(progress) >= progressToAutoCompleteStep &&
        !autocompleteStepFiredRef.current
      ) {
        autocompleteStepFiredRef.current = true;
        void resolveCurrentStep();
      }
    },
    [
      requiredCompletionPercentage,
      setVideoStepAccepted,
      resolveCurrentStep,
      currentStep,
    ],
  );

  const onVideoEnd = React.useCallback(() => {
    if (isAutoPlayOn) {
      if (document.fullscreenElement) {
        document
          .exitFullscreen()
          .then(() => {
            setIsAutoplayStateEnabled(true);
          })
          .catch((err) => console.error(err));
      } else {
        setIsAutoplayStateEnabled(true);
      }
    }
  }, [isAutoPlayOn, setIsAutoplayStateEnabled]);

  const onVideoPlayerError = React.useCallback(
    (err: any) => {
      if (err?.name === 'PlayInterrupted' || err?.code === 'UNKNOWN') {
        return;
      }

      setIsVideoLoadingError(true);
      setVideoStepAccepted(false);
      console.error(err);
    },
    [setIsVideoLoadingError, setVideoStepAccepted],
  );

  const reset = React.useCallback(() => {
    setIsVideoLoadingError(false);
    setVideoStepAccepted(!requiredCompletionPercentage);
    setIsAutoplayStateEnabled(false);
    resetVideoState();
  }, [requiredCompletionPercentage, resetVideoState]);

  const getHLSStreamURL = React.useCallback(() => {
    void requestHLSStreamURL?.(step?.id);
  }, [step?.id]);

  React.useEffect(() => {
    if (isVideo) {
      reset();
      void requestVideoData?.(step?.id);
    }

    return reset;
  }, [step?.id]);

  React.useEffect(() => {
    // When the request for video data is ended and the video is started playing, we need to reset the autoplay state.
    if (!isVideoRequestInProgress && videoData) {
      setTimeout(() => {
        void changeAutoplayOptions(false);
      }, 1000);
    }
  }, [isVideoRequestInProgress]);

  React.useEffect(() => {
    if (videoDataRequestError || HLSStreamURLRequestError) {
      setIsVideoLoadingError(true);
    }
  }, [videoDataRequestError, HLSStreamURLRequestError]);

  return isVideo ? (
    <div
      ref={playerRef}
      className={st(classes.root, {
        mobile: isMobile,
        withAutoPlayScreen: isAutoplayStateEnabled && !isLastEntity,
      })}
    >
      {videoData && !isVideoLoadingError ? (
        <>
          <VideoAutoplayScreen
            isEnabled={isAutoplayStateEnabled}
            onCancel={() => {
              setIsAutoplayStateEnabled(false);
            }}
          />
          <ProgramsVideoPlayer
            isMobile={isMobile}
            isAutoplay={isAutoPlayVideo}
            customCoverUrl={customCoverUrl}
            videoData={videoData as any}
            onPlayerReady={(player: any) => {
              if (isMobile) {
                const videoTag = playerRef?.current?.querySelector('video');

                videoTag?.setAttribute('muted', '');
                videoTag?.setAttribute('playsInline', '');

                // super-puper mute
                player.mute?.();
              }
            }}
            onRequestHLSStreamURL={getHLSStreamURL as () => Promise<void>}
            HLSStreamURL={HLSStreamURL}
            onProgress={onVideoPlayerProgress}
            onEnd={onVideoEnd}
            onError={onVideoPlayerError}
            disableSeekForward={shouldDisableSeekForward}
          />
        </>
      ) : null}
      {isVideoLoadingError ? <VideoError type="NOT_FOUND" /> : null}
    </div>
  ) : null;
};
